import React, { useEffect, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { Tab, TabList, TabPanel, Tabs } from 'carbon-react/lib/components/tabs/__next__';
import Typography from 'carbon-react/lib/components/typography';
import Button from 'carbon-react/lib/components/button';
import TokensWrapper from 'carbon-react/lib/components/tokens-wrapper';

import { UISystemIcon } from '@futrli/ui-components/components/icons/custom/system-icon';
import { InsightsTab } from '../../insights-tab';
import { SummaryTab } from '../../summary-tab';
import { AiInsightsData, InsightsVariant } from '../../ai-insights';
import { HealthScoreCalculationDialog } from './health-score-calculation-dialog';
import { HealthScoreSummary } from './health-score-summary';
import * as styles from './insights-modal.scss';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface InsightsModalProps {
  isOpen: boolean;
  onClose: () => void;
  variant: InsightsVariant;
  data: AiInsightsData;
}

// ─── Component ───────────────────────────────────────────────────────────────
export const InsightsModal = ({ isOpen, onClose, variant, data }: InsightsModalProps) => {
  const intl = useIntl();
  const dialogRef = useRef<HTMLDivElement>(null);
  const isAccountant = variant === 'accountant';

  const tabIds = isAccountant
    ? { first: 'detailed-insights', second: 'executive-summary' }
    : { first: 'things-to-know', second: 'the-big-picture' };

  const [selectedTab, setSelectedTab] = useState(tabIds.first);
  const [isCalculationModalOpen, setIsCalculationModalOpen] = useState(false);

  // Reset to first tab whenever modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedTab(tabIds.first);
      setIsCalculationModalOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isCalculationModalOpen) {
        onClose();
      }
    },
    [isCalculationModalOpen, onClose]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) {
    return null;
  }

  const firstTabLabel = isAccountant
    ? intl.formatMessage({ id: 'aiInsights.tab.detailedInsights', defaultMessage: 'Detailed Insights' })
    : intl.formatMessage({ id: 'aiInsights.tab.thingsToKnow', defaultMessage: 'Things to Know' });

  const secondTabLabel = isAccountant
    ? intl.formatMessage({ id: 'aiInsights.tab.executiveSummary', defaultMessage: 'Executive Summary' })
    : intl.formatMessage({ id: 'aiInsights.tab.theBigPicture', defaultMessage: 'The Big Picture' });

  return createPortal(
    <TokensWrapper>
      <HealthScoreCalculationDialog
        isOpen={isCalculationModalOpen}
        score={data.healthScore?.score ?? 0}
        scoreDeltas={data.healthScore?.scoreDeltas ?? []}
        onClose={() => setIsCalculationModalOpen(false)}
      />

      {/* Backdrop — click outside closes */}
      <div className={styles.backdrop} onClick={e => e.target === e.currentTarget && onClose()}>
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="insights-modal-title"
          className={styles.dialog}
          tabIndex={-1}
          onClick={e => e.stopPropagation()}
        >
          {/* ── Header ───────────────────────────────────────────────────── */}
          <header className={styles.header}>
            <UISystemIcon iconName="ai-sparkle" width={30} height={30} />
            <Typography id="insights-modal-title" fontSize="24px" fontWeight="700" lineHeight="125%" mb={0}>
              {intl.formatMessage({
                id: 'aiInsights.healthCard.title',
                defaultMessage: 'Business Health'
              })}
            </Typography>

            <span className={styles.headerSpacer} aria-hidden="true" />

            <button
              type="button"
              className={styles.headerCloseButton}
              onClick={onClose}
              aria-label={intl.formatMessage({ id: 'common.close', defaultMessage: 'Close' })}
            >
              <span aria-hidden="true">{'\u2715'}</span>
            </button>
          </header>

          {/* ── Content: tabbed ──────────────────────────────────────────── */}
          <div className={styles.content}>
            <div className={styles.carbonTabsContainer}>
              <Tabs selectedTabId={selectedTab}>
                <TabList
                  ariaLabel={intl.formatMessage({
                    id: 'aiInsights.healthCard.title',
                    defaultMessage: 'Business Health'
                  })}
                  onTabChange={(tabId: string) => setSelectedTab(tabId)}
                >
                  <Tab id={tabIds.first} controls={`${tabIds.first}-panel`} label={firstTabLabel} />
                  <Tab id={tabIds.second} controls={`${tabIds.second}-panel`} label={secondTabLabel} />
                </TabList>
                <TabPanel id={`${tabIds.first}-panel`} tabId={tabIds.first}>
                  <div className={styles.tabContent}>
                    {data.healthScore && (
                      <>
                        <HealthScoreSummary
                          score={data.healthScore.score}
                          status={data.healthScore.overallStatus}
                          onHowCalculated={() => setIsCalculationModalOpen(true)}
                        />
                        <hr className={styles.insightsDivider} />
                      </>
                    )}
                    <InsightsTab insights={data.insights} />
                  </div>
                </TabPanel>
                <TabPanel id={`${tabIds.second}-panel`} tabId={tabIds.second}>
                  <div className={styles.tabContent}>
                    <SummaryTab data={data.executiveSummary} />
                  </div>
                </TabPanel>
              </Tabs>
            </div>
          </div>

          {/* ── Footer ───────────────────────────────────────────────────── */}
          <footer className={styles.footer}>
            <Button
              buttonType="primary"
              onClick={onClose}
              className={styles.footerButton}
              data-testid="insights-modal-close"
            >
              {intl.formatMessage({ id: 'common.close', defaultMessage: 'Close' })}
            </Button>
          </footer>
        </div>
      </div>
    </TokensWrapper>,
    document.body
  );
};
